import { Fragment, useEffect, useMemo, useState } from 'react'
import { router, useForm, usePage } from '@inertiajs/react'
import { Link } from '@inertiajs/react'
import _ from 'lodash'
import Button from '@/Shared/Button'
import Checkbox from '@/Shared/Forms/Checkbox'
import Table from '@/Shared/Table/Index'
import TextInput from '@/Shared/Forms/TextInput'

const Breadcrumbs = ({ activeChecklist, activePropertyType, onChecklistsClicked, onPropertyTypesClicked }) => {
  return (
    <ul className="flex items-center gap-3">
      {activeChecklist ? (
        <Fragment>
          <li>
            <Link
              role="button"
              className="h-6 rounded-md px-0.5 leading-none text-blue-500 outline-none transition-all duration-150 ease-in-out hover:bg-blue-100 hover:text-gray-800 focus:ring-2 focus:ring-blue-500"
              onClick={(e) => onChecklistsClicked(e)}
            >
              <span className="font-medium">Checklists</span>
            </Link>
          </li>

          <li>
            <i className="fas fa-angle-right text-xs text-gray-400"></i>
          </li>

          <li>
            {activePropertyType ? (
              <Link
                role="button"
                className="inline-flex h-6 items-center gap-4 rounded-md px-0.5 leading-none text-blue-500 outline-none transition-all duration-150 ease-in-out hover:bg-blue-100 hover:text-gray-800 focus:ring-2 focus:ring-blue-500"
                onClick={(e) => onPropertyTypesClicked(e)}
              >
                <span className="font-medium">{activeChecklist}</span>
              </Link>
            ) : (
              <span>{activeChecklist}</span>
            )}
          </li>

          <li>
            <i className="fas fa-angle-right text-xs text-gray-400"></i>
          </li>
        </Fragment>
      ) : (
        <Fragment>
          <li>
            <span>Checklists</span>
          </li>

          <li>
            <i className="fas fa-angle-right text-xs text-gray-400"></i>
          </li>
        </Fragment>
      )}

      {activePropertyType && (
        <Fragment>
          <li>
            <span>{activePropertyType}</span>
          </li>

          <li>
            <i className="fas fa-angle-right text-xs text-gray-400"></i>
          </li>
        </Fragment>
      )}
    </ul>
  )
}

const Items = ({ items, selected, onClick }) => {
  return (
    <Table>
      <Table.Header>
        <tr>
          <th className="h-12 cursor-pointer rounded-t bg-gray-200 px-4 font-medium uppercase text-gray-800 sm:px-6" colSpan="2">
            <div className="flex items-center leading-none">Name</div>
          </th>
        </tr>
      </Table.Header>
      <Table.Body>
        {items.map((item) => (
          <tr className="text-gray-700 hover:bg-gray-100" key={item.value} onClick={() => onClick(item)}>
            <td className="cursor-pointer px-4 py-3 font-medium sm:px-6">
              <div className="flex items-center gap-2">
                <span className="font-bold text-black">{item.label}</span>
                {selected && <span className="text-sm font-normal text-gray-500">{selected}</span>}
              </div>
            </td>

            <td className="w-16 cursor-pointer whitespace-nowrap py-3 pr-6 text-right font-medium">
              <div className="flex items-center justify-end">
                <i className="far fa-angle-right text-2xl leading-none text-gray-700"></i>
              </div>
            </td>
          </tr>
        ))}
      </Table.Body>
    </Table>
  )
}

export default function TransactionSupportTabView() {
  const { contact, checklist_types, property_types, states: allStates, constants } = usePage().props
  const [originalData, setOriginalData] = useState(() =>
    _.cloneDeep({
      transaction_support_enabled: contact.associate.transaction_support_enabled,
      transaction_support_lookback_months:
        contact.associate.transaction_support_lookback_months || constants.transaction_support.months_elapsed,
      transaction_support: contact.associate.transaction_support,
    }),
  )

  const { data, setData } = useForm(originalData)
  const [activeChecklist, setActiveChecklist] = useState(null)
  const [activePropertyType, setActivePropertyType] = useState(null)
  const [isModified, setIsModified] = useState(false)
  const transactionSupportData = contact.associate.transaction_support

  const states = allStates
    .filter((state) =>
      contact.associate?.licenses?.some((license) => {
        return license.state.includes(state.code)
      }),
    )
    .map((state) => {
      const checklist = transactionSupportData.find((c) => c.id === activeChecklist?.value)
      const property = checklist?.property_types.find((p) => p.id === activePropertyType?.value)
      const match = property?.states.find((s) => s.id === state.id)

      return {
        ...state,
        required: match?.required ?? constants.transaction_support.min_required,
      }
    })

  useEffect(() => {
    setIsModified(!_.isEqual(data, originalData))
  }, [data, originalData])

  const onRequiredTransactionsValueChanged = (state, value) => {
    const checklistId = activeChecklist.value
    const propertyTypeId = activePropertyType.value
    const stateId = state.id

    const existingChecklist = data.transaction_support?.find((c) => c.id === checklistId)
    const existingPropertyType = existingChecklist?.property_types.find((p) => p.id === propertyTypeId)
    const existingState = existingPropertyType?.states.find((s) => s.id === stateId)

    if (!existingState) {
      addNewEntry(state, value)
    } else {
      updateExistingEntry(state, value)
    }
  }

  const addNewEntry = (state, value) => {
    setData((prev) => {
      const checklistId = activeChecklist.value
      const propertyTypeId = activePropertyType.value

      const support = [...prev.transaction_support]
      const checklist = support.find((c) => c.id === checklistId)
      const newState = {
        id: state.id,
        code: state.code,
        name: state.name,
        required: value,
      }

      if (!checklist) {
        // Checklist doesn't exist — append full structure
        support.push({
          id: checklistId,
          name: activeChecklist.label,
          property_types: [
            {
              id: propertyTypeId,
              name: activePropertyType.label,
              states: [newState],
            },
          ],
        })
      } else {
        const property = checklist.property_types.find((p) => p.id === propertyTypeId)
        if (!property) {
          // Property type doesn't exist — append it with state
          checklist.property_types.push({
            id: propertyTypeId,
            name: activePropertyType.label,
            states: [newState],
          })
        } else {
          // Append new state only
          property.states.push(newState)
        }
      }

      return {
        ...prev,
        transaction_support: support,
      }
    })
  }

  const updateExistingEntry = (state, value) => {
    setData((prev) => ({
      ...prev,
      transaction_support: prev.transaction_support.map((checklist) => {
        if (checklist.id !== activeChecklist.value) return checklist

        return {
          ...checklist,
          property_types: checklist.property_types.map((propertyType) => {
            if (propertyType.id !== activePropertyType.value) return propertyType

            return {
              ...propertyType,
              states: propertyType.states.map((stateObj) => {
                if (stateObj.id !== state.id) return stateObj

                return {
                  ...stateObj,
                  required: value,
                }
              }),
            }
          }),
        }
      }),
    }))
  }

  const submit = (event) => {
    event.preventDefault()

    router.post(
      route('admin.associates.update', contact.associate.id),
      {
        ...data,
        _method: 'put',
      },
      {
        onFinish: () => {
          setOriginalData(_.cloneDeep(data))
        },
      },
    )
  }

  return (
    <form id="transaction-support-form" className="flex flex-col overflow-y-auto px-6 pb-10 pt-1" onSubmit={submit}>
      <div className="space-y-6">
        <Checkbox
          name="enabled_transaction_support"
          label="Enabled"
          value={data.transaction_support_enabled}
          onChange={(checked) => {
            setData({ ...data, transaction_support_enabled: checked })
          }}
        />

        {data.transaction_support_enabled && (
          <div className="space-y-4">
            <hr />

            <div className="space-y-3">
              <Breadcrumbs
                activeChecklist={activeChecklist?.label}
                activePropertyType={activePropertyType?.label}
                onChecklistsClicked={(e) => {
                  e.preventDefault()
                  setActiveChecklist(null)
                  setActivePropertyType(null)
                }}
                onPropertyTypesClicked={(e) => {
                  e.preventDefault()
                  setActivePropertyType(null)
                }}
              />

              {!activeChecklist ? (
                <Items items={checklist_types} onClick={(value) => setActiveChecklist(value)} />
              ) : !activePropertyType ? (
                <Items items={property_types} selected={activeChecklist.label} onClick={(value) => setActivePropertyType(value)} />
              ) : (
                <Table>
                  <Table.Header>
                    <tr>
                      <th className="h-12 rounded-t bg-gray-200 px-4 font-medium uppercase text-gray-800 sm:px-6">
                        <div className="flex items-center leading-none">State</div>
                      </th>

                      <th className="bg-gray-200 py-2 pr-4 text-center">
                        <span># of Required Transactions</span>
                      </th>
                    </tr>
                  </Table.Header>
                  <Table.Body>
                    {states.map((state) => (
                      <tr className="text-gray-700" key={state.id}>
                        <td className="px-4 py-3 font-medium sm:px-6">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-black">{state.name}</span>
                            <span className="text-sm font-normal text-gray-500">
                              {activeChecklist.label} <i className="fal fa-angle-right px-0.5 text-sm"></i> {activePropertyType.label}
                            </span>
                          </div>
                        </td>

                        <td width="100px" className="py-2 pr-4">
                          <TextInput
                            classes="mb-0 w-16 mx-auto"
                            inputClasses="text-center"
                            value={
                              data.transaction_support
                                ?.find((c) => c.id === activeChecklist.value)
                                ?.property_types.find((p) => p.id === activePropertyType.value)
                                ?.states.find((s) => s.id === state.id)?.required ??
                              state.required ??
                              constants.transaction_support.min_required
                            }
                            onChange={(value) => onRequiredTransactionsValueChanged(state, value)}
                          />
                        </td>
                      </tr>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center justify-end border-t border-gray-200 pt-4">
          {data.transaction_support_enabled && (
            <div className="grow">
              <TextInput
                className="mb-0"
                label="# of past months to check"
                name="transaction_support_lookback_months"
                value={data.transaction_support_lookback_months}
                onChange={(value) => setData({ ...data, transaction_support_lookback_months: value })}
              />
            </div>
          )}

          <div className="flex justify-end">
            <Button theme="outline" disabled={!isModified || !data.transaction_support_enabled} onClick={() => setData(originalData)}>
              Reset
            </Button>

            <Button type="submit" theme="solid" form="transaction-support-form" disabled={!isModified}>
              Save
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}
